import React from 'react';
import { FiTarget, FiEye, FiHeart, FiShield, FiCheckCircle, FiUsers } from 'react-icons/fi';
import Card from '@components/common/Card';
import SectionHeader from '@components/common/SectionHeader';
import { MISSION, VISION, VALUES } from '@utils/constants';

/**
 * MissionVision Component
 * Homepage section displaying mission, vision, and values
 */
const MissionVision = () => {
  // Map icon names to components
  const iconMap = {
    shield: FiShield,
    eye: FiCheckCircle,
    checkCircle: FiCheckCircle,
    heart: FiHeart,
  };

  // Enhanced values with descriptions
  const valuesDetails = [
    {
      name: 'Integrity',
      icon: FiShield,
      description: 'We uphold the highest standards of honesty and ethical conduct in all our operations.',
      color: 'primary',
    },
    {
      name: 'Transparency',
      icon: FiEye,
      description: 'We maintain open communication and clear processes with all our stakeholders.',
      color: 'secondary',
    },
    {
      name: 'Accountability',
      icon: FiCheckCircle,
      description: 'We take responsibility for our actions and deliver on our commitments.',
      color: 'primary',
    },
    {
      name: 'Honesty',
      icon: FiHeart,
      description: 'We build trust through truthful and sincere relationships with our members.',
      color: 'secondary',
    },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <SectionHeader
          subtitle="Our Purpose"
          title="Driven by Mission, Guided by Values"
          description="Our commitment to excellence and farmer empowerment shapes everything we do"
          className="mb-16"
        />

        {/* Mission & Vision Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Mission Card */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-bukawa-primary to-bukawa-primary-700 rounded-2xl opacity-5 group-hover:opacity-10 transition-opacity" />
            <Card padding="lg" className="relative h-full border-2 border-bukawa-primary-100 hover:border-bukawa-primary-200 transition-colors">
              {/* Icon */}
              <div className="inline-flex p-4 bg-bukawa-primary text-white rounded-xl mb-6 shadow-lg">
                <FiTarget className="h-8 w-8" />
              </div>

              {/* Title */}
              <h3 className="text-2xl font-heading font-bold text-bukawa-primary mb-4">
                Our Mission
              </h3>

              {/* Mission Statement */}
              <p className="text-bukawa-gray-700 leading-relaxed text-lg">
                {MISSION}
              </p>

              {/* Decorative Element */}
              <div className="absolute top-8 right-8 w-32 h-32 bg-bukawa-primary rounded-full opacity-5 blur-3xl" />
            </Card>
          </div>

          {/* Vision Card */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-bukawa-secondary to-bukawa-secondary-600 rounded-2xl opacity-5 group-hover:opacity-10 transition-opacity" />
            <Card padding="lg" className="relative h-full border-2 border-bukawa-secondary-100 hover:border-bukawa-secondary-200 transition-colors">
              {/* Icon */}
              <div className="inline-flex p-4 bg-bukawa-secondary text-bukawa-primary-900 rounded-xl mb-6 shadow-lg">
                <FiEye className="h-8 w-8" />
              </div>

              {/* Title */}
              <h3 className="text-2xl font-heading font-bold text-bukawa-primary mb-4">
                Our Vision
              </h3>

              {/* Vision Statement */}
              <p className="text-bukawa-gray-700 leading-relaxed text-lg">
                {VISION}
              </p>

              {/* Decorative Element */}
              <div className="absolute top-8 right-8 w-32 h-32 bg-bukawa-secondary rounded-full opacity-5 blur-3xl" />
            </Card>
          </div>
        </div>

        {/* Values Section */}
        <div className="mt-20">
          {/* Values Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="h-px w-8 bg-bukawa-secondary" />
              <span className="text-bukawa-secondary font-medium text-sm uppercase tracking-wider">
                Our Core Values
              </span>
              <span className="h-px w-8 bg-bukawa-secondary" />
            </div>
            <h3 className="text-3xl md:text-4xl font-heading font-bold text-bukawa-gray-900">
              What We Stand For
            </h3>
          </div>

          {/* Values Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {valuesDetails.map((value, index) => {
              const Icon = value.icon;
              const colorClasses = {
                primary: {
                  bg: 'bg-bukawa-primary-50',
                  icon: 'text-bukawa-primary',
                  border: 'border-bukawa-primary-100 hover:border-bukawa-primary-200',
                  glow: 'bg-bukawa-primary',
                },
                secondary: {
                  bg: 'bg-bukawa-secondary-50',
                  icon: 'text-bukawa-secondary-700',
                  border: 'border-bukawa-secondary-100 hover:border-bukawa-secondary-200',
                  glow: 'bg-bukawa-secondary',
                },
              };

              const colors = colorClasses[value.color];

              return (
                <div
                  key={index}
                  className="relative group"
                >
                  <Card
                    hover
                    className={`text-center h-full border-2 ${colors.border} transition-all duration-300`}
                  >
                    {/* Icon Circle */}
                    <div className={`relative inline-flex p-5 ${colors.bg} rounded-full mb-4`}>
                      <Icon className={`h-8 w-8 ${colors.icon}`} />
                      {/* Glow Effect */}
                      <div className={`absolute inset-0 ${colors.glow} rounded-full blur-xl opacity-0 group-hover:opacity-20 transition-opacity`} />
                    </div>

                    {/* Value Name */}
                    <h4 className="text-xl font-semibold text-bukawa-gray-900 mb-3">
                      {value.name}
                    </h4>

                    {/* Description */}
                    <p className="text-sm text-bukawa-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Quote Section */}
        <div className="mt-20">
          <Card className="bg-gradient-to-r from-bukawa-primary to-bukawa-primary-700 text-white text-center">
            <div className="max-w-4xl mx-auto py-8">
              <FiUsers className="h-12 w-12 mx-auto mb-6 opacity-80" />
              <blockquote className="text-2xl md:text-3xl font-heading font-semibold mb-6 leading-relaxed">
                "Together, we are building a sustainable future for our farmers 
                and communities through quality, collaboration, and commitment."
              </blockquote>
              <div className="flex items-center justify-center gap-2">
                <span className="h-px w-12 bg-white/50" />
                <span className="text-bukawa-secondary font-medium">
                  Bukawa ACE Ltd
                </span>
                <span className="h-px w-12 bg-white/50" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default MissionVision;